import { Fragment, type JSX, useId } from 'react';

import { useTelefon } from './telefon';

/**
 * DAS DIAGRAMM DER BERICHTE (AUS-16, Auftrag #24 und #26) — eigene SVG-Bausteine.
 *
 * ============================================================================
 * KEIN FREMDPAKET
 * ============================================================================
 *
 * Balken und Linie sind wenige Rechtecke und Pfade. Ein Diagrammpaket
 * braechte hundert Kilobyte, eine eigene Farbwelt und eigene Schriften — und
 * jede davon muesste man gegen Abschnitt 2.10 wieder zuruecknehmen. Die
 * Farben kommen aus den Klassen in stil.css und damit aus denselben
 * Merkmalswerten wie jede andere Flaeche.
 *
 * ============================================================================
 * ZWEI ACHSEN, BIS ZU DREI ZEITRAEUME (Auftrag #26)
 * ============================================================================
 *
 * Jede Reihe hat eine ACHSE (links oder rechts), einen TON (je Kennzahl eine
 * Reihenfarbe, die erste in der Handlungsfarbe — Auftrag CRM-42) und eine
 * STUFE (0 der Zeitraum selbst, 1 und 2 die Vergleiche). Woran man eine Reihe
 * erkennt, steht dreifach da — nicht nur in der Farbe (WCAG 1.4.1):
 *
 *   - die Kennzahlen der linken Achse mit runden Punkten, die der rechten mit
 *     eckigen;
 *   - Vergleiche als hellere Balken, als Linie gestrichelt (1) oder
 *     gepunktet (2);
 *   - ueber jeder Achse ihr Titel in der Farbe ihrer Reihe, und die Legende
 *     sagt "linke Achse" bzw. "rechte Achse".
 *
 * ============================================================================
 * UNVOLLSTAENDIG IST SCHRAFFIERT (AUS-10)
 * ============================================================================
 *
 * Der laufende Monat bekommt denselben Balken, aber schraffiert; auf der Linie
 * ist sein Punkt hohl und der Weg dorthin gestrichelt. Ein halber September
 * neben einem vollen August sieht sonst aus wie ein Einbruch.
 *
 * ============================================================================
 * BARRIEREFREI (NFA-14)
 * ============================================================================
 *
 * Das Bild traegt `role="img"` und einen Namen. Die Zahlen selbst stehen
 * daneben als Tabelle (`Ergebnistabelle`) — sie ist die Alternative, die ein
 * Vorleseprogramm liest und die man mit der Tastatur erreicht. Die kleine
 * Fassung in der Berichtsliste ist Schmuck neben dem Text der Kachel und fuer
 * Vorlesehilfen verborgen.
 *
 * ============================================================================
 * AUF DEM TELEFON (Auftrag CRM-106, ÄA-63)
 * ============================================================================
 *
 * Bei 390 px schrumpfte das Bild von 680 Einheiten die Achsenschrift auf
 * rund 6 px. Unter 600 px (`useTelefon`) zeichnet es deshalb auf einer
 * schmalen Flaeche (`SCHMAL`) — die Schrift steht dann fast 1 : 1 —, mit
 * weniger Beschriftungen an der Kategorieachse. Die Werte stehen sonst nur
 * im Tooltip, den ein Finger nicht erreicht; darunter klappt "Werte" eine
 * kleine Tabelle auf. Am Desktop und auf dem iPad bleibt alles, wie es war.
 */

export type Diagrammpunkt = {
  readonly schluessel: string;
  /** Die Beschriftung an der Achse (kurz). */
  readonly beschriftung: string;
  /** null: kein Wert (eine Quote ohne Nenner). Gezeichnet wird dann nichts. */
  readonly wert: number | null;
  readonly unvollstaendig: boolean;
  /** Der gesetzte Wert fuer den Hinweis beim Ueberfahren. */
  readonly wertText: string;
};

/**
 * Die Farbe einer Kennzahl im Bild: ihre Stelle in der Beschreibung, 1 bis 10
 * (`--reihe-N` in stil.css, Auftrag CRM-42). Sie folgt der Kennzahl und nicht
 * ihrer Achse — rueckt eine Achse nach, behaelt jede Reihe ihre Farbe.
 */
export type Reihenfarbe = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

/** Eine Reihe des Diagramms (Auftrag #26). */
export type Reihe = {
  readonly name: string;
  readonly achse: 'links' | 'rechts';
  readonly art: 'balken' | 'linie';
  /**
   * Die Kennzahl: ihre Reihenfarbe; `ziel` die Ziellinie der ersten Kennzahl
   * (Auftrag #29, AUS-12) — gestrichelt in Rost, ohne Punkte.
   */
  readonly ton: Reihenfarbe | 'ziel';
  /** 0 der Zeitraum selbst, 1 und 2 die Vergleiche. */
  readonly stufe: 0 | 1 | 2;
  /** Je Kategorie ein Wert, in der Folge der Kategorien. */
  readonly werte: readonly (number | null)[];
  readonly texte: readonly string[];
  /** AUS-10: nur fuer den Zeitraum selbst. */
  readonly unvollstaendig?: readonly boolean[];
  /**
   * AUS-34 (ÄA-56): je Kategorie die Lage der Einzelwerte — Min bis Max,
   * Q1 bis Q3, Median. Nur am Zeitraum selbst, nie an einem Vergleich.
   */
  readonly lagen?: readonly (Lagewert | null)[];
  /** AUS-34: je Kategorie der Anteil als Beschriftung („12 %"). */
  readonly anteile?: readonly (string | null)[];
  /** AUS-34: je Kategorie, was die Kurzinfo ueber den Wert hinaus nennt („n 12 · Anteil 12 %"). */
  readonly zusatz?: readonly (string | null)[];
  /** AUS-34: die laufende Summe „zusaetzlich" — eigenes Strichmuster, keine Flaeche. */
  readonly kumuliert?: boolean;
};

/**
 * AUS-34 (ÄA-56): die Lage einer Zeile. Was nicht gewaehlt ist, ist null —
 * nur Min und Max ergeben die Linie mit Enden, Quartile den Kasten, der
 * Median den Querstrich.
 */
export type Lagewert = {
  readonly min: number | null;
  readonly max: number | null;
  readonly q1: number | null;
  readonly q3: number | null;
  readonly median: number | null;
  /** Die Werte als Satz fuer Kurzinfo und Vorlesehilfe. */
  readonly text: string;
};

/** Die Zahlen einer Lage, die die Skala einschliessen muss. */
function lagezahlen(p: Lagewert | null): number[] {
  return p === null ? [] : [p.min, p.max, p.q1, p.q3, p.median].flatMap((w) => (w === null ? [] : [w]));
}

export type Kategorie = { readonly schluessel: string; readonly beschriftung: string };

/** Eine "schoene" obere Grenze: 1, 2, 2,5, 5 oder 10 mal einer Zehnerpotenz. */
function schoeneGrenze(x: number): number {
  if (x <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(x));
  const n = x / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * p;
}

function kuerzen(text: string, laenge: number): string {
  return text.length > laenge ? `${text.slice(0, laenge - 1)}…` : text;
}

/**
 * DIE SKALA DER KLEINEN FASSUNG (Auftrag #40): von null (oder dem kleinsten
 * Wert darunter) bis genau zum groessten Wert. Die kleine Fassung zeigt
 * keine Achse und kein Raster — eine "schoene" Grenze darueber liesse nur
 * leere Flaeche stehen, die niemand ablesen kann. Der hoechste Balken reicht
 * deshalb bis oben. Sind alle Werte null, steht nichts (hoch 1).
 */
export function kleineSkala(werte: readonly number[]): { tief: number; hoch: number } {
  const tief = Math.min(0, ...werte);
  const hoch = Math.max(0, ...werte);
  return { tief, hoch: hoch > tief ? hoch : tief + 1 };
}

/** Wie hoch ein Wert in der kleinen Fassung steht: 0 unten, 1 oben. */
export function kleinerAnteil(skala: { tief: number; hoch: number }, wert: number): number {
  return (wert - skala.tief) / (skala.hoch - skala.tief);
}

/** Die Skala einer Achse: von der kleinsten bis zur groessten Zahl, null immer dabei. */
function skala(werte: readonly number[]): { tief: number; hoch: number } {
  const hoch = schoeneGrenze(Math.max(0, ...werte) * 1.05);
  const min = Math.min(0, ...werte);
  return { tief: min < 0 ? -schoeneGrenze(-min * 1.05) : 0, hoch };
}

type Einfach = {
  readonly art: 'balken' | 'linie';
  readonly punkte: readonly Diagrammpunkt[];
};

type Mehrfach = {
  readonly kategorien: readonly Kategorie[];
  readonly reihen: readonly Reihe[];
  /** Wie ein Wert an der rechten Achse steht; ohne rechte Reihe unbenutzt. */
  readonly achseRechts?: (wert: number) => string;
  /** Die Titel der Achsen — der Name der Kennzahl mit Einheit. */
  readonly achsentitel?: { readonly links: string; readonly rechts?: string };
  /**
   * Auftrag CRM-46: Die Balken stehen je Kategorie aufeinander statt
   * nebeneinander — positive nach oben, negative nach unten. Die Skala
   * reicht dann bis zur Summe jeder Kategorie.
   */
  readonly gestapelt?: boolean;
};

/**
 * Auftrag CRM-46: Wo jeder Balken eines Stapels beginnt — je Reihe und
 * Kategorie der Sockel, dazu je Kategorie die Summen beider Richtungen.
 */
export function stapelsockel(
  reihen: readonly Reihe[],
  kategorien: number,
): { readonly sockel: readonly (readonly number[])[]; readonly oben: readonly number[]; readonly unten: readonly number[] } {
  const oben = Array.from({ length: kategorien }, () => 0);
  const unten = Array.from({ length: kategorien }, () => 0);
  const sockel = reihen.map((r) =>
    oben.map((_, i) => {
      const w = r.werte[i] ?? null;
      if (w === null) return 0;
      const basis = w >= 0 ? oben[i]! : unten[i]!;
      if (w >= 0) oben[i] = basis + w;
      else unten[i] = basis + w;
      return basis;
    }),
  );
  return { sockel, oben, unten };
}

/** Die Breite der kleinen Fassung in Einheiten des Bilds; gestreckt wird sie auf die Breite des Felds. */
const KLEIN_BREITE = 240;

/**
 * Auftrag CRM-106: die Flaeche auf dem Telefon — so breit, wie das Bild bei
 * 360 bis 390 px ungefaehr Platz hat. Die Schrift wird damit kaum verkleinert.
 */
const SCHMAL = { breite: 340, hoehe: 230, achse: 50 } as const;

/** Auftrag CRM-106: so viel Platz braucht eine Beschriftung der Kategorieachse auf dem Telefon. */
const SCHMAL_BESCHRIFTUNG = 56;

/** Wie breit eine Gruppe nebeneinanderstehender Balken ist und wie breit jeder darin. */
function balkenmass(schritt: number, nebeneinander: number, klein: boolean): { gruppenbreite: number; balkenbreite: number } {
  const gruppenbreite = Math.min((klein ? 18 : 46) * Math.max(1, nebeneinander * 0.8), schritt * 0.72);
  return { gruppenbreite, balkenbreite: gruppenbreite / Math.max(1, nebeneinander) };
}

/**
 * Auftrag CRM-60: die Mitte des ERSTEN Balkens der Spalte `i` in der kleinen
 * Fassung, als Anteil der Breite (0 bis 1). Das Berichtswidget setzt den Wert
 * des Zeitraums darüber; stehen Vergleichsbalken daneben, ist das nicht mehr
 * die Mitte der Spalte.
 */
export function kleineBalkenmitte(i: number, n: number, nebeneinander: number): number {
  const schritt = KLEIN_BREITE / Math.max(n, 1);
  const { gruppenbreite, balkenbreite } = balkenmass(schritt, nebeneinander, true);
  const breite = Math.max(1, balkenbreite - (nebeneinander > 1 ? 2 : 0));
  return (schritt * (i + 0.5) - gruppenbreite / 2 + breite / 2) / KLEIN_BREITE;
}

export function Diagramm(
  eingabe: (Einfach | Mehrfach) & {
    /** Der Name des Bildes fuer Vorlesehilfen: „Umsatz netto je Monat". */
    readonly beschriftung: string;
    /** Wie ein Wert an der (linken) Achse steht: „12 T €". */
    readonly achse: (wert: number) => string;
    readonly klein?: boolean;
    /**
     * Nur die kleine Fassung: je Kategorie der Hinweis beim Ueberfahren
     * („Oktober 2026, läuft noch: 12,0 Tage", Auftrag #40). Das Bild bleibt
     * fuer Vorlesehilfen verborgen; die Zahlen stehen daneben als Text.
     */
    readonly hinweise?: readonly string[];
    /** Auftrag CRM-47: die Gitterlinien hinter dem Bild; die Zahlen an der Achse bleiben. */
    readonly gitter?: boolean;
    /**
     * Auftrag CRM-47: der Wert an jedem Balken und an jedem Punkt des
     * Zeitraums — kurz wie an der Achse; gestapelt die Summe ueber dem Stapel.
     */
    readonly mitWerten?: boolean;
  },
): JSX.Element {
  const { beschriftung, achse, klein = false, hinweise, gitter = true } = eingabe;
  const schraffur = useId().replace(/:/g, '');
  /* Auftrag CRM-106: die schmale Flaeche nur fuer das grosse Bild — die kleine Fassung streckt sich ohnehin. */
  const schmal = useTelefon() && !klein;

  /* Die einfache Form (eine Reihe) ist die Mehrfachform mit einer Reihe. */
  const { kategorien, reihen }: { readonly kategorien: readonly Kategorie[]; readonly reihen: readonly Reihe[] } =
    'punkte' in eingabe
      ? {
          kategorien: eingabe.punkte.map((p) => ({ schluessel: p.schluessel, beschriftung: p.beschriftung })),
          reihen: [
            {
              name: beschriftung,
              achse: 'links' as const,
              art: eingabe.art,
              ton: 1 as const,
              stufe: 0 as const,
              werte: eingabe.punkte.map((p) => p.wert),
              texte: eingabe.punkte.map((p) => p.wertText),
              unvollstaendig: eingabe.punkte.map((p) => p.unvollstaendig),
            },
          ],
        }
      : eingabe;
  const achseRechts = 'punkte' in eingabe ? undefined : eingabe.achseRechts;
  const titel = 'punkte' in eingabe ? undefined : eingabe.achsentitel;
  const gestapelt = 'punkte' in eingabe ? false : eingabe.gestapelt === true;
  /* Gestapelt werden die Balken der linken Achse; die rechte traegt nur Linien. */
  const balkenreihen = reihen.filter((r) => r.art === 'balken');
  const stapel = gestapelt ? stapelsockel(balkenreihen, kategorien.length) : null;

  const mitRechts = reihen.some((r) => r.achse === 'rechts');
  /* Auftrag CRM-106: Auf dem Telefon steht der Titel der rechten Achse in einer eigenen Zeile. */
  const titelZweizeilig = schmal && mitRechts && titel?.rechts !== undefined;
  const B = klein ? KLEIN_BREITE : schmal ? SCHMAL.breite : 680;
  const H = klein ? 54 : schmal ? SCHMAL.hoehe : 270;
  const links = klein ? 0 : schmal ? SCHMAL.achse : 62;
  const rechts = klein ? 0 : mitRechts ? (schmal ? SCHMAL.achse : 62) : 8;
  const oben = klein ? 0 : titel === undefined ? 10 : titelZweizeilig ? 38 : 24;
  const unten = klein ? 0 : 28;
  const ib = B - links - rechts;
  const ih = H - oben - unten;

  const werteVon = (seite: 'links' | 'rechts'): number[] =>
    reihen
      .filter((r) => r.achse === seite && (stapel === null || r.art !== 'balken'))
      .flatMap((r) => [
        ...r.werte.flatMap((w) => (w === null ? [] : [w])),
        // AUS-34: Die Achse schliesst Min und Max ein — sonst ragen die Enden hinaus.
        ...(r.lagen ?? []).flatMap(lagezahlen),
      ])
      .concat(stapel === null || seite === 'rechts' ? [] : [...stapel.oben, ...stapel.unten]);
  const skalaVon = klein ? kleineSkala : skala;
  const skalen = { links: skalaVon(werteVon('links')), rechts: skalaVon(werteVon('rechts')) };
  const y = (seite: 'links' | 'rechts', w: number): number => {
    const { tief, hoch } = skalen[seite];
    return oben + ih - ((w - tief) / (hoch - tief)) * ih;
  };

  const n = Math.max(kategorien.length, 1);
  const schritt = ib / n;
  const mitte = (i: number): number => links + schritt * (i + 0.5);

  /* Bei vielen Kategorien nur jede k-te Beschriftung — sonst stehen sie aufeinander. */
  const jede = klein
    ? 0
    : schmal
      ? Math.max(1, Math.ceil(n / Math.max(1, Math.floor(ib / SCHMAL_BESCHRIFTUNG))))
      : Math.max(1, Math.ceil(n / 12));
  /* Auftrag CRM-106: auf dem Telefon so kurz, wie der Platz bis zur naechsten Beschriftung reicht. */
  const zeichenJeBeschriftung = schmal ? Math.max(4, Math.floor((schritt * jede) / 7)) : 14;
  /*
   * Auftrag CRM-47: der Wert an jedem Balken. Auf dem Telefon nur, wo er neben
   * dem Nachbarn Platz hat (CRM-106) — sonst stehen die Zahlen aufeinander;
   * die Tabelle "Werte" unter dem Bild nennt sie dann.
   */
  const wertmarken = !klein && eingabe.mitWerten === true && (!schmal || schritt >= 40);
  const linien = klein ? [] : [0, 1, 2, 3, 4];
  const stufe = (s: number, seite: 'links' | 'rechts'): number =>
    skalen[seite].tief + ((skalen[seite].hoch - skalen[seite].tief) * s) / 4;

  /* Balkenreihen stehen je Kategorie nebeneinander, Zeitraum zuerst — gestapelt als ein Balken. */
  const nebeneinander = stapel === null ? balkenreihen.length : 1;
  const { gruppenbreite, balkenbreite } = balkenmass(schritt, nebeneinander, klein);

  const hinweis = (r: Reihe, i: number): string =>
    `${r.name} · ${kategorien[i]?.beschriftung ?? ''}: ${r.texte[i] ?? ''}${
      r.unvollstaendig?.[i] === true ? ' (unvollständig)' : ''
    }${r.zusatz?.[i] == null ? '' : ` · ${r.zusatz[i]!}`}`;
  const formatVon = (r: Reihe): ((wert: number) => string) => (r.achse === 'rechts' ? (achseRechts ?? achse) : achse);

  const bild = (
    <svg
      className={klein ? 'diagramm diagramm--klein' : 'diagramm'}
      viewBox={`0 0 ${String(B)} ${String(H)}`}
      preserveAspectRatio={klein ? 'none' : 'xMidYMid meet'}
      {...(klein
        ? { 'aria-hidden': true, focusable: 'false' }
        : { role: 'img', 'aria-label': beschriftung })}
    >
      {!klein && <title>{beschriftung}</title>}
      <defs>
        {/* Je Reihenfarbe ein Muster: Die laufende Periode einer orangen Reihe ist orange schraffiert. */}
        {[...new Set(balkenreihen.flatMap((r) => (r.ton === 'ziel' ? [] : [r.ton])))].map((ton) => (
          <pattern
            key={ton}
            id={`${schraffur}-${String(ton)}`}
            data-reihe={ton}
            width="6"
            height="6"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <rect className="diagramm__schraffur-grund" width="6" height="6" />
            <rect className="diagramm__schraffur-strich" width="3" height="6" />
          </pattern>
        ))}
      </defs>

      {titel !== undefined && !klein && (
        <>
          <text className="diagramm__achsentitel" x={links} y={12} textAnchor="start">
            {schmal ? kuerzen(titel.links, 40) : titel.links}
          </text>
          {mitRechts && titel.rechts !== undefined && (
            <text
              className="diagramm__achsentitel diagramm__achsentitel--zwei"
              x={B - rechts}
              y={titelZweizeilig ? 27 : 12}
              textAnchor="end"
            >
              {schmal ? kuerzen(titel.rechts, 40) : titel.rechts}
            </text>
          )}
        </>
      )}

      {linien.map((s) => (
        <g key={s}>
          {gitter && (
            <line className="diagramm__raster" x1={links} x2={B - rechts} y1={y('links', stufe(s, 'links'))} y2={y('links', stufe(s, 'links'))} />
          )}
          <text className="diagramm__achse" x={links - (schmal ? 6 : 8)} y={y('links', stufe(s, 'links')) + 4} textAnchor="end">
            {achse(stufe(s, 'links'))}
          </text>
          {mitRechts && (
            <text
              className="diagramm__achse diagramm__achse--zwei"
              x={B - rechts + (schmal ? 6 : 8)}
              y={y('rechts', stufe(s, 'rechts')) + 4}
              textAnchor="start"
            >
              {(achseRechts ?? achse)(stufe(s, 'rechts'))}
            </text>
          )}
        </g>
      ))}

      {/*
        DIE NULLLINIE EIGENS: Bei negativen Werten liegt null nicht auf einer
        der vier Rasterlinien — ohne sie haengen die Balken in der Luft.
      */}
      {!klein && <line className="diagramm__nulllinie" x1={links} x2={B - rechts} y1={y('links', 0)} y2={y('links', 0)} />}

      {balkenreihen.map((r, b) =>
        r.werte.map((w, i) => {
          if (w === null) return null;
          const x = mitte(i) - gruppenbreite / 2 + (stapel === null ? b : 0) * balkenbreite;
          const basis = stapel?.sockel[b]?.[i] ?? 0;
          const y0 = y(r.achse, basis);
          const y1 = y(r.achse, basis + w);
          const unvollstaendig = r.stufe === 0 && r.unvollstaendig?.[i] === true;
          const breite = Math.max(1, balkenbreite - (nebeneinander > 1 ? 2 : 0));
          return (
            <Fragment key={`${r.name}-${kategorien[i]?.schluessel ?? String(i)}`}>
            <rect
              className={`diagramm__balken diagramm__balken--stufe${String(r.stufe)}`}
              data-reihe={r.ton === 'ziel' ? undefined : r.ton}
              x={x}
              y={Math.min(y0, y1)}
              width={breite}
              height={Math.max(Math.abs(y0 - y1), w === 0 ? 0 : 1)}
              /*
                ALS STIL UND NICHT ALS ATTRIBUT: Die Klasse setzt `fill` in stil.css,
                und eine CSS-Regel schlaegt das Darstellungsattribut — die Schraffur
                war so unsichtbar (gefunden im Bildvergleich, 29.09.2026).
              */
              {...(unvollstaendig ? { style: { fill: `url(#${schraffur}-${String(r.ton)})` } } : {})}
            >
              {!klein && <title>{hinweis(r, i)}</title>}
            </rect>
            {/* Gestapelt steht die Summe ueber dem Stapel (unten); nebeneinander jeder Wert an seinem Balken. */}
            {wertmarken && stapel === null && (
              <text
                className="diagramm__wert"
                x={x + breite / 2}
                y={w >= 0 ? Math.min(y0, y1) - 4 : Math.max(y0, y1) + 11}
                textAnchor="middle"
              >
                {formatVon(r)(w)}
              </text>
            )}
            {/* AUS-34: der Anteil — gestapelt im Segment, nebeneinander ueber dem Balken (und ueber dem Wert). */}
            {!klein && r.anteile?.[i] != null && (stapel === null || Math.abs(y0 - y1) >= 14) && (
              <text
                className={stapel === null ? 'diagramm__anteil' : 'diagramm__anteil diagramm__anteil--innen'}
                x={x + breite / 2}
                y={
                  stapel !== null
                    ? (y0 + y1) / 2 + 4
                    : w >= 0
                      ? Math.min(y0, y1) - 4 - (wertmarken ? 12 : 0)
                      : Math.max(y0, y1) + 11 + (wertmarken ? 12 : 0)
                }
                textAnchor="middle"
              >
                {r.anteile[i]}
              </text>
            )}
            </Fragment>
          );
        }),
      )}

      {wertmarken
        && stapel !== null
        && kategorien.map((k, i) => {
          const oben = stapel.oben[i] ?? 0;
          const unten = stapel.unten[i] ?? 0;
          if (oben === 0 && unten === 0) return null;
          const summe = oben + unten;
          return (
            <text
              key={`summe-${k.schluessel}`}
              className="diagramm__wert"
              x={mitte(i)}
              y={oben > 0 ? y('links', oben) - 4 : y('links', unten) + 11}
              textAnchor="middle"
            >
              {achse(summe)}
            </text>
          );
        })}

      {reihen
        .filter((r) => r.art === 'linie')
        .map((r) => {
          const mitWert = r.werte.flatMap((w, i) => (w === null ? [] : [{ w, i }]));
          if (mitWert.length === 0) return null;
          const klasse = `diagramm__linie${r.ton === 'ziel' ? ' diagramm__linie--ziel' : ''} diagramm__linie--stufe${String(r.stufe)}${r.kumuliert === true ? ' diagramm__linie--kumuliert' : ''}`;
          const lagen = klein ? [] : (r.lagen ?? []);
          return (
            <g key={r.name} data-reihe={r.ton === 'ziel' ? undefined : r.ton}>
              {/*
                AUS-34 (ÄA-56): an der Linie die Lage als Baender — hell von Min
                bis Max, dunkler von Q1 bis Q3 — und der Median gestrichelt.
                Ein Band laeuft nur ueber benachbarte Werte; ein einzelner Wert
                bekommt die Zeichen wie am Balken.
              */}
              {(['spanne', 'quartil'] as const).map((band) =>
                abschnitte(lagen, (p) => (band === 'spanne' ? [p.min, p.max] : [p.q1, p.q3])).map((a) =>
                  a.length === 1 ? null : (
                    <path
                      key={`${band}-${String(a[0]!.i)}`}
                      className={`diagramm__band diagramm__band--${band}`}
                      d={`M${a.map(({ i, von }) => `${String(mitte(i))},${String(y(r.achse, von))}`).join(' L')} L${[...a]
                        .reverse()
                        .map(({ i, bis }) => `${String(mitte(i))},${String(y(r.achse, bis))}`)
                        .join(' L')} Z`}
                    />
                  ),
                ),
              )}
              {abschnitte(lagen, (p) => [p.median, p.median]).map((a) =>
                a.length === 1 ? null : (
                  <path
                    key={`median-${String(a[0]!.i)}`}
                    className="diagramm__medianlinie"
                    d={`M${a.map(({ i, von }) => `${String(mitte(i))},${String(y(r.achse, von))}`).join(' L')}`}
                  />
                ),
              )}
              {lagen.map((p, i) =>
                p === null || !einzeln(lagen, i) ? null : (
                  <Lagezeichen key={`lage-${String(i)}`} p={p} x={mitte(i)} breite={Math.min(24, schritt * 0.5)} y={(w) => y(r.achse, w)} ton={r.ton} />
                ),
              )}
              {r.ton === 1 && r.stufe === 0 && r.kumuliert !== true && (
                <path
                  className="diagramm__flaeche"
                  d={`M${String(mitte(mitWert[0]!.i))},${String(y(r.achse, 0))} ${mitWert
                    .map(({ w, i }) => `L${String(mitte(i))},${String(y(r.achse, w))}`)
                    .join(' ')} L${String(mitte(mitWert[mitWert.length - 1]!.i))},${String(y(r.achse, 0))} Z`}
                />
              )}
              {mitWert.slice(1).map(({ w, i }, k) => {
                const vor = mitWert[k]!;
                const offen = r.stufe === 0 && r.unvollstaendig?.[i] === true;
                return (
                  <line
                    key={`${r.name}-${String(i)}`}
                    className={offen ? `${klasse} diagramm__linie--unvollstaendig` : klasse}
                    x1={mitte(vor.i)}
                    y1={y(r.achse, vor.w)}
                    x2={mitte(i)}
                    y2={y(r.achse, w)}
                  />
                );
              })}
              {/* Punkte nur fuer den Zeitraum selbst: rund auf der linken Achse, eckig auf der rechten. */}
              {!klein
                && r.stufe === 0
                && r.ton !== 'ziel'
                && mitWert.map(({ w, i }) => {
                  const offen = r.unvollstaendig?.[i] === true;
                  const klassePunkt = `diagramm__punkt${r.achse === 'rechts' ? ' diagramm__punkt--eckig' : ''}${offen ? ' diagramm__punkt--unvollstaendig' : ''}`;
                  const punkt = r.achse === 'links' ? (
                    <circle className={klassePunkt} cx={mitte(i)} cy={y(r.achse, w)} r={3.5}>
                      <title>{hinweis(r, i)}</title>
                    </circle>
                  ) : (
                    <rect className={klassePunkt} x={mitte(i) - 3.5} y={y(r.achse, w) - 3.5} width={7} height={7}>
                      <title>{hinweis(r, i)}</title>
                    </rect>
                  );
                  return (
                    <Fragment key={`${r.name}-${String(i)}`}>
                      {punkt}
                      {wertmarken && (
                        <text className="diagramm__wert" x={mitte(i)} y={y(r.achse, w) - 8} textAnchor="middle">
                          {formatVon(r)(w)}
                        </text>
                      )}
                      {/* AUS-34 (ÄA-56): der Anteil unter dem Punkt. */}
                      {r.anteile?.[i] != null && (
                        <text className="diagramm__anteil" x={mitte(i)} y={y(r.achse, w) + 16} textAnchor="middle">
                          {r.anteile[i]}
                        </text>
                      )}
                    </Fragment>
                  );
                })}
            </g>
          );
        })}

      {/* Die kleine Fassung hat keine Punkte: Je Spalte eine unsichtbare Flaeche traegt den Hinweis. */}
      {klein
        && hinweise !== undefined
        && kategorien.map((k, i) => (
          <rect key={`treffer-${k.schluessel}`} className="diagramm__treffer" x={mitte(i) - schritt / 2} y={0} width={schritt} height={H}>
            <title>{hinweise[i] ?? ''}</title>
          </rect>
        ))}

      {/*
        AUS-34 (ÄA-56): die Lage am Balken — Linie mit Enden von Min bis Max,
        schmaler Kasten Q1 bis Q3, Querstrich fuer den Median. Form statt
        Farbe: Die Legende erklaert die Zeichen, die Kurzinfo nennt die Werte.
      */}
      {!klein
        && balkenreihen.map((r, b) =>
          (r.lagen ?? []).map((p, i) => {
            if (p === null || stapel !== null) return null;
            const breite = Math.max(1, balkenbreite - (nebeneinander > 1 ? 2 : 0));
            const x = mitte(i) - gruppenbreite / 2 + b * balkenbreite + breite / 2;
            return (
              <Lagezeichen key={`lage-${r.name}-${String(i)}`} p={p} x={x} breite={breite} y={(w) => y(r.achse, w)} ton={r.ton} />
            );
          }),
        )}

      {!klein
        && kategorien.map((k, i) =>
          i % jede === 0 ? (
            <text key={k.schluessel} className="diagramm__achse" x={mitte(i)} y={H - 8} textAnchor="middle">
              {kuerzen(k.beschriftung, zeichenJeBeschriftung)}
            </text>
          ) : null,
        )}
    </svg>
  );
  if (!schmal) return bild;
  return (
    <>
      {bild}
      <Wertetabelle kategorien={kategorien} reihen={reihen} />
    </>
  );
}

/**
 * Auftrag CRM-106 (ÄA-63): die Werte des Bilds auf dem Telefon — dort, wo der
 * Tooltip nicht hinreicht. Zugeklappt, damit sie das Bild nicht verdraengt;
 * je Kategorie eine Zeile, je Reihe eine Spalte, mit dem Text des Tooltips.
 */
function Wertetabelle({
  kategorien,
  reihen,
}: {
  readonly kategorien: readonly Kategorie[];
  readonly reihen: readonly Reihe[];
}): JSX.Element {
  const zelle = (r: Reihe, i: number): string => {
    if ((r.werte[i] ?? null) === null) return '—';
    return [
      `${r.texte[i] ?? ''}${r.unvollstaendig?.[i] === true ? ' (unvollständig)' : ''}`,
      r.zusatz?.[i] ?? null,
      r.lagen?.[i]?.text ?? null,
    ]
      .filter((t): t is string => t !== null && t !== '')
      .join(' · ');
  };
  return (
    <details className="diagramm__werte">
      <summary>Werte</summary>
      <div className="tabellenhuelle">
        <table className="liste__tabelle">
          <thead>
            <tr>
              <th scope="col">
                <span className="nur-vorlesen">Kategorie</span>
              </th>
              {reihen.map((r) => (
                <th key={r.name} scope="col">
                  {r.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {kategorien.map((k, i) => (
              <tr key={k.schluessel}>
                <th scope="row">{k.beschriftung}</th>
                {reihen.map((r) => (
                  <td key={r.name} className="zahl">
                    {zelle(r, i)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

/** Ein Wert mit Lage an der Linie: Spalte und die beiden Grenzen des Bands. */
type Bandpunkt = { readonly i: number; readonly von: number; readonly bis: number };

/**
 * AUS-34 (ÄA-56): die Abschnitte eines Bands an der Linie — aufeinander
 * folgende Spalten, in denen beide Grenzen bekannt sind. Eine Luecke (kein
 * Wert, Option nicht gewaehlt) teilt das Band, statt es ueber sie zu ziehen.
 */
function abschnitte(lagen: readonly (Lagewert | null)[], grenzen: (p: Lagewert) => readonly [number | null, number | null]): Bandpunkt[][] {
  const aus: Bandpunkt[][] = [];
  let lauf: Bandpunkt[] = [];
  lagen.forEach((p, i) => {
    const [von, bis] = p === null ? [null, null] : grenzen(p);
    if (von === null || bis === null) {
      if (lauf.length > 0) aus.push(lauf);
      lauf = [];
      return;
    }
    lauf.push({ i, von, bis });
  });
  if (lauf.length > 0) aus.push(lauf);
  return aus;
}

/** Ob die Lage einer Spalte allein steht — kein Nachbar mit Lage, also kein Band. */
function einzeln(lagen: readonly (Lagewert | null)[], i: number): boolean {
  return (lagen[i - 1] ?? null) === null && (lagen[i + 1] ?? null) === null;
}

/**
 * AUS-34 (ÄA-56): die Lagezeichen eines Werts — fuer stehende Balken
 * senkrecht, fuer liegende (`liegend`) waagerecht. `x` ist die Mitte quer zur
 * Werteachse, `wo` rechnet einen Wert in die Lage auf der Werteachse um.
 * Erreichbar mit der Tastatur; die Kurzinfo nennt die Zahlen.
 */
export function Lagezeichen({
  p,
  x,
  breite,
  y: wo,
  ton,
  liegend = false,
}: {
  readonly p: Lagewert;
  readonly x: number;
  readonly breite: number;
  readonly y: (wert: number) => number;
  readonly ton: Reihenfarbe | 'ziel';
  readonly liegend?: boolean;
}): JSX.Element {
  const ende = Math.min(8, breite / 3);
  const kasten = Math.max(4, Math.min(16, breite * 0.45));
  /* Eine Strecke quer (`quer` true: ueber die Breite) oder entlang der Werteachse. */
  const strich = (von: number, bis: number, mitte: number, halb: number, klasse: string, quer: boolean): JSX.Element => {
    const [x1, x2, y1, y2] = quer ? [mitte - halb, mitte + halb, von, bis] : [mitte, mitte, von, bis];
    return liegend ? <line className={klasse} x1={y1} x2={y2} y1={x1} y2={x2} /> : <line className={klasse} x1={x1} x2={x2} y1={y1} y2={y2} />;
  };
  return (
    <g className="diagramm__lage" data-reihe={ton === 'ziel' ? undefined : ton} tabIndex={0} role="img" aria-label={p.text}>
      <title>{p.text}</title>
      {p.min !== null && p.max !== null && (
        <>
          {strich(wo(p.max), wo(p.min), x, 0, 'diagramm__whisker', false)}
          {strich(wo(p.max), wo(p.max), x, ende, 'diagramm__whisker', true)}
          {strich(wo(p.min), wo(p.min), x, ende, 'diagramm__whisker', true)}
        </>
      )}
      {p.q1 !== null && p.q3 !== null && (() => {
        const a = Math.min(wo(p.q1), wo(p.q3));
        const l = Math.max(1, Math.abs(wo(p.q1) - wo(p.q3)));
        return liegend ? (
          <rect className="diagramm__kasten" x={a} width={l} y={x - kasten / 2} height={kasten} />
        ) : (
          <rect className="diagramm__kasten" x={x - kasten / 2} width={kasten} y={a} height={l} />
        );
      })()}
      {p.median !== null && strich(wo(p.median), wo(p.median), x, Math.max(kasten / 2 + 2, ende), 'diagramm__median', true)}
    </g>
  );
}

/** Ein Eintrag der Legende: wie die Reihe aussieht und wozu sie gehoert. */
export type Legendeneintrag = {
  readonly text: string;
  /** AUS-34: die Zeichen der Kennzahloptionen erklaeren sich mit einem kleinen Bild. */
  readonly muster: 'balken' | 'linie' | 'unvollstaendig' | Lagemuster;
  /** AUS-34: die laufende Summe mit eigenem Strichmuster. */
  readonly kumuliert?: boolean;
  readonly ton?: Reihenfarbe | 'ziel';
  readonly stufe?: 0 | 1 | 2;
};

/** AUS-34: die Zeichen der Lage in der Legende — an Balken und an der Linie. */
export type Lagemuster = 'spanne' | 'kasten' | 'median' | 'band-spanne' | 'band-quartil' | 'medianlinie';

const LAGEMUSTER: readonly string[] = ['spanne', 'kasten', 'median', 'band-spanne', 'band-quartil', 'medianlinie'];

/** Das kleine Bild eines Lagezeichens in der Legende. */
function Lagemusterbild({ muster, ton }: { readonly muster: Lagemuster; readonly ton: Reihenfarbe | 'ziel' | undefined }): JSX.Element {
  return (
    <svg className="legende__lage" width="22" height="12" viewBox="0 0 22 12" aria-hidden="true" data-reihe={ton === 'ziel' ? undefined : (ton ?? 1)}>
      {muster === 'spanne' && (
        <>
          <line className="diagramm__whisker" x1={2} x2={20} y1={6} y2={6} />
          <line className="diagramm__whisker" x1={2} x2={2} y1={2} y2={10} />
          <line className="diagramm__whisker" x1={20} x2={20} y1={2} y2={10} />
        </>
      )}
      {muster === 'kasten' && <rect className="diagramm__kasten" x={4} y={2} width={14} height={8} />}
      {muster === 'median' && <line className="diagramm__median" x1={4} x2={18} y1={6} y2={6} />}
      {muster === 'band-spanne' && <rect className="diagramm__band diagramm__band--spanne" x={1} y={1} width={20} height={10} />}
      {muster === 'band-quartil' && <rect className="diagramm__band diagramm__band--quartil" x={1} y={3} width={20} height={6} />}
      {muster === 'medianlinie' && <line className="diagramm__medianlinie" x1={1} x2={21} y1={6} y2={6} />}
    </svg>
  );
}

/** Die Legende unter dem Diagramm. Ohne `eintraege` die einfache Form: Wert und Unvollständiges. */
export function Legende({
  wert,
  mitUnvollstaendig,
  eintraege,
}: {
  readonly wert?: string;
  readonly mitUnvollstaendig?: boolean;
  readonly eintraege?: readonly Legendeneintrag[];
}): JSX.Element {
  const liste: readonly Legendeneintrag[] = eintraege ?? [
    { text: wert ?? '', muster: 'balken', ton: 1, stufe: 0 },
    ...(mitUnvollstaendig === true
      ? [{ text: 'noch nicht abgeschlossen', muster: 'unvollstaendig' as const }]
      : []),
  ];
  return (
    <ul className="legende" aria-label="Legende">
      {liste.map((e) => (
        <li key={e.text} className="legende__eintrag">
          {LAGEMUSTER.includes(e.muster) ? (
            <Lagemusterbild muster={e.muster as Lagemuster} ton={e.ton} />
          ) : (
          <span
            className={
              e.muster === 'unvollstaendig'
                ? 'legende__farbe legende__farbe--unvollstaendig'
                : e.muster === 'balken'
                  ? `legende__farbe legende__farbe--wert legende__farbe--stufe${String(e.stufe ?? 0)}`
                  : `legende__linie${e.ton === 'ziel' ? ' legende__linie--ziel' : ''} legende__linie--stufe${String(e.stufe ?? 0)}${e.kumuliert === true ? ' legende__linie--kumuliert' : ''}`
            }
            data-reihe={e.muster === 'unvollstaendig' || e.ton === 'ziel' ? undefined : (e.ton ?? 1)}
            aria-hidden="true"
          />
          )}
          {e.text}
        </li>
      ))}
    </ul>
  );
}

/* ── Das Kreisdiagramm (29.09.2026, Wunsch des Nutzers zu Auftrag #26) ─────── */

/** Ein Stueck des Kreises: seine Gruppe, ihr Wert und der gesetzte Wert. */
export type Kreisstueck = {
  readonly schluessel: string;
  readonly beschriftung: string;
  readonly wert: number;
  readonly wertText: string;
};

/** Wie viele Stuecke einzeln stehen; der Rest wird zu „Übrige". */
export const HOECHSTE_STUECKE = 7;

/**
 * Die Stuecke fuer den Kreis: die groessten sieben, der Rest zusammengefasst.
 *
 * MEHR ALS SIEBEN FARBEN UNTERSCHEIDET NIEMAND. Die Farben kommen aus den
 * Merkmalswerten des Hauses (2.10) und nicht aus einer eigenen Palette; der
 * Rest steht als achtes, hellgraues Stueck „Übrige (n)". Die Tabelle
 * darunter zeigt jede Gruppe einzeln.
 */
export function kreisstuecke(
  stuecke: readonly Kreisstueck[],
  restText: (summe: number) => string,
): readonly Kreisstueck[] {
  const sortiert = [...stuecke].filter((s) => s.wert > 0).sort((a, b) => b.wert - a.wert);
  if (sortiert.length <= HOECHSTE_STUECKE + 1) return sortiert;
  const rest = sortiert.slice(HOECHSTE_STUECKE);
  const summe = rest.reduce((a, s) => a + s.wert, 0);
  return [
    ...sortiert.slice(0, HOECHSTE_STUECKE),
    { schluessel: '__uebrige', beschriftung: `Übrige (${String(rest.length)})`, wert: summe, wertText: restText(summe) },
  ];
}

const ANTEIL = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/**
 * DER KREIS: Anteile der Gruppen an der Summe. Beschriftet wird nicht im
 * Kreis, sondern in der Legende daneben — mit Farbe, Name, Wert und Anteil;
 * schmale Stuecke haetten sonst unlesbare Beschriftungen. Die Stuecke
 * trennt eine weisse Fuge, damit auch benachbarte helle Toene unterscheidbar
 * bleiben (WCAG 1.4.11).
 */
export function Kreisdiagramm({
  stuecke,
  beschriftung,
  ohneLegende = false,
  klein = false,
  anteileImBild = false,
}: {
  readonly stuecke: readonly Kreisstueck[];
  readonly beschriftung: string;
  /** AUS-34 (ÄA-56): der Anteil als Beschriftung der Stücke — wenn die Kennzahl ihn trägt. */
  readonly anteileImBild?: boolean;
  /** Auftrag CRM-47: ohne die Liste daneben — die Zahlen stehen in der Tabelle. */
  readonly ohneLegende?: boolean;
  readonly klein?: boolean;
}): JSX.Element {
  const summe = stuecke.reduce((a, s) => a + s.wert, 0);
  const r = klein ? 25 : 100;
  const c = klein ? 27 : 110;
  const punkt = (winkel: number): string =>
    `${String(c + r * Math.cos(winkel))},${String(c + r * Math.sin(winkel))}`;

  let winkel = -Math.PI / 2;
  /* AUS-34 (ÄA-56): Anteile ab 5 % stehen im Stück; kleinere nennt die Liste daneben. */
  const anteile: JSX.Element[] = [];
  const formen = stuecke.map((s, i) => {
    const klasse = `diagramm__stueck diagramm__stueck--${String(s.schluessel === '__uebrige' ? 8 : i + 1)}`;
    const teil = summe === 0 ? 0 : s.wert / summe;
    if (teil >= 0.9999) {
      if (anteileImBild && !klein) {
        anteile.push(
          <text key={`anteil-${s.schluessel}`} className="diagramm__stueckanteil" x={c} y={c + 4} textAnchor="middle" aria-hidden="true">
            100 %
          </text>,
        );
      }
      return <circle key={s.schluessel} className={klasse} cx={c} cy={c} r={r} />;
    }
    const von = winkel;
    winkel += teil * 2 * Math.PI;
    if (anteileImBild && !klein && teil >= 0.05) {
      const mitte = (von + winkel) / 2;
      anteile.push(
        <text
          key={`anteil-${s.schluessel}`}
          className="diagramm__stueckanteil"
          x={c + r * 0.65 * Math.cos(mitte)}
          y={c + r * 0.65 * Math.sin(mitte) + 4}
          textAnchor="middle"
          aria-hidden="true"
        >
          {`${ANTEIL.format(teil * 100)} %`}
        </text>,
      );
    }
    return (
      <path
        key={s.schluessel}
        className={klasse}
        d={`M${String(c)},${String(c)} L${punkt(von)} A${String(r)},${String(r)} 0 ${teil > 0.5 ? 1 : 0} 1 ${punkt(winkel)} Z`}
      >
        {!klein && <title>{`${s.beschriftung}: ${s.wertText} (${ANTEIL.format(teil * 100)} %)`}</title>}
      </path>
    );
  });

  const bild = (
    <svg
      className={klein ? 'diagramm diagramm--klein diagramm--kreis' : 'diagramm diagramm--kreis'}
      viewBox={`0 0 ${String(c * 2)} ${String(c * 2)}`}
      {...(klein ? { 'aria-hidden': true, focusable: 'false' } : { role: 'img', 'aria-label': beschriftung })}
    >
      {!klein && <title>{beschriftung}</title>}
      {formen}
      {anteile}
    </svg>
  );
  if (klein || ohneLegende) return bild;

  return (
    <div className="kreis">
      {bild}
      <ul className="kreis__legende" aria-label="Legende">
        {stuecke.map((s, i) => (
          <li key={s.schluessel} className="kreis__eintrag">
            <span
              className={`legende__farbe legende__farbe--stueck${String(s.schluessel === '__uebrige' ? 8 : i + 1)}`}
              aria-hidden="true"
            />
            <span className="kreis__name">{s.beschriftung}</span>
            <span className="kreis__wert">{s.wertText}</span>
            <span className="kreis__anteil">{summe === 0 ? '—' : `${ANTEIL.format((s.wert / summe) * 100)} %`}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
